/**
 * DeletedNoteCard - the grey marker a deleted note leaves on the timeline, where the note was
 * (docs/plans/crm-report-builder-search-collab-plan.md A1.2, A1.3 steps 3 and 5, A5 N3 a and N4 a, B1.4;
 * WP N-7): "Note deleted by Dana Smith, Sep 26 3:14 PM: Duplicate".
 *
 * The reason rides the row itself: crm.delete_note stamps metadata.deleted_reason_code and
 * metadata.deleted_reason_text (the latter absent when none was given; plan B13, N-1), and the words
 * come from deletedMarkerWords (noteLogic.ts): the reason's label, or the Other sentence. The row's body
 * is the empty string; this card never shows it.
 *
 * "View deleted text" is offered to CRM admins only (useIsCRMAdmin, display only: the server decides
 * and audits every view) and opens NoteHistoryModal, which reads the history fresh each time.
 */
import { useState } from 'react';
import { Eye, Trash2 } from 'lucide-react';
import type { Communication } from '../types';
import { useIsCRMAdmin } from '../lib';
import NoteHistoryModal from './NoteHistoryModal';
import { deletedMarkerWords, noteActions } from './noteLogic';
import type { DeletedMarkerOptions, NoteWordsOptions } from './noteLogic';

export interface DeletedNoteCardProps {
  contactId: string;
  /** The deleted note's timeline row (deleted_at, deleted_by, metadata.deleted_reason_*). */
  note: Communication;
  /** A user id to a name (useTeam().byId): names the deleter. Without it: "a teammate". */
  nameOf?: NoteWordsOptions['nameOf'];
  /** Display-only override of useIsCRMAdmin() (the server decides who reads the words). */
  isAdmin?: boolean;
  /** Extra classes for the marker (margins). */
  className?: string;
}

/** The deletion's reason from the row's metadata, as deletedMarkerWords takes it; null when none. */
export function deletedReasonOf(note: Pick<Communication, 'metadata'> | null | undefined): DeletedMarkerOptions['reason'] {
  const meta = note?.metadata;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return null;
  const code = typeof meta.deleted_reason_code === 'string' ? meta.deleted_reason_code.trim() : '';
  if (!code) return null;
  const text = typeof meta.deleted_reason_text === 'string' ? meta.deleted_reason_text : null;
  return { reason_code: code, reason_text: text };
}

export default function DeletedNoteCard({ contactId, note, nameOf, isAdmin, className = '' }: DeletedNoteCardProps) {
  const adminByRole = useIsCRMAdmin();
  const [viewing, setViewing] = useState(false);
  const admin = isAdmin ?? adminByRole;
  const canView = noteActions(note, { userId: null, isAdmin: admin }).canViewDeletedWords;
  const words = deletedMarkerWords(note, { nameOf, reason: deletedReasonOf(note) });

  return (
    <>
      <div
        data-note-id={note.id}
        data-note-state="deleted"
        className={`flex items-center gap-2 rounded-lg border-l-2 border-gray-300 bg-gray-50 px-3 py-1.5 dark:border-gray-600 dark:bg-gray-800/60 ${className}`}
      >
        <Trash2 className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        <p className="min-w-0 flex-1 break-words text-xs italic text-gray-500 dark:text-gray-400">{words}</p>
        {canView && (
          <button
            type="button"
            onClick={() => setViewing(true)}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0 sm:py-1"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            View deleted text
          </button>
        )}
      </div>
      {canView && (
        <NoteHistoryModal
          open={viewing}
          onClose={() => setViewing(false)}
          contactId={contactId}
          noteId={note.id}
          nameOf={nameOf}
          title="Deleted note"
        />
      )}
    </>
  );
}
